import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import Clipboard from 'react-clipboard.js';

import ShareIcon from './Svg/ShareIcon';
import SocialShareGroup from './SocialShareFooter/SocialShareGroup';
import BackButton from './Buttons/BackButton';

import { onFindYourRep } from '../containers/LocalRep/actions';
import {
    getNextAction,
    buildConditionalActionsAnswerHistory
} from '../utils/nextActionUtil';

import {
    closeShareDrawer,
    copyToClipboard,
    openShareDrawer,
    setOverlayVisible
} from '../containers/Widget/actions';

import { CardTypes } from '../utils/enums';
import { width, widthEm, isPortrait } from '../utils/size';
import staticData from '../static_data/all';
import { copyFor } from '../copy/copy';

const Wrapper = styled.div`
    width: 100%;
    display: flex;
    position: relative;
    justify-content: space-between;
    align-items: center;
    z-index: ${props => (props.shareDrawerVisibility ? '3' : '1')};
    padding: 0 0.5em;
    background: ${props =>
        props.backgroundColor
            ? props.backgroundColor
            : props.hasNextAction
            ? '#f8f8f7'
            : '#fff'};
`;

const Container = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: ${props =>
        (props.localRepFound && !props.isEmailSubmitted) ||
        props.newsletterSignUpVisible ||
        props.useLocalRepresentative ||
        props.fullwidth
            ? props.rightAlign
                ? `flex-end`
                : `space-between`
            : `flex-end`};
    width: 100%;
    align-items: center;

    .emailSignup {
        padding-left: ${props =>
            props.newsletterSignUpVisible &&
            props.isActionChoiceCard &&
            props.hasBackButton &&
            !isPortrait()
                ? 80
                : 0}px;
    }

    a {
        font-family: Lato;
        font-size: 0.625em;
        line-height: 1.6;
        color: #008eff;
        text-decoration: none;
        padding: 5px;
        white-space: nowrap;
        margin-left: 10px;
        cursor: pointer;

        svg {
            position: relative;
            top: 2px;
        }
    }
`;

const revealShareDrawerContent = y => keyframes`
    0% {
      transform: translateY(0)
    }

    100% {
      transform: translateY(${y})
    }
`;

const ShareDrawerContent = styled.div`
    position: absolute;
    left: -1em;
    width: calc(100% + 2em);
    padding: 16px 18px;
    background: #fff;
    z-index: 3;
    ${props => `transform: translateY(${props.drawerHeight})`};
    animation: ${props => revealShareDrawerContent(props.drawerHeight)} 0.3s linear
        alternate;
`;

const ShareSection = styled.div`
    width: 174px;
    float: left;

    @media (max-width: 30em) {
        width: 100%;
    }
`;

const EmbedSection = styled.div`
    width: calc(100% - 204px);
    float: left;
    margin-left: 30px;
    position: relative;

    @media (max-width: 30em) {
        width: 100%;
        margin-left: 0;
        margin-top: 12px;
    }
`;

const ShareBackground = styled.div`
    background: #000;
    position: fixed;
    left: 5px;
    top: 2px;
    width: calc(100% - 10px);
    height: calc(100% - 4px);
    transition: opacity 0.3s;
    opacity: 0;
    visibility: hidden;

    @media (max-width: 30em) {
        left: 0;
        width: 100%;
    }

    ${props =>
        props.shareDrawerVisibility &&
        ` opacity: 0.5;
          visibility: visible;
        `};
`;

const Title = styled.h2`
    font-family: Averta;
    font-size: 16px;
    font-weight: normal;
    line-height: 1.63;
    color: #000000;
    margin: 0;
`;

const SocialShareContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex: 1;
    align-items: flex-start;
    margin-top: 8px;
`;

const Input = styled.input`
    border-radius: 2px;
    border: solid 1px #f1f1ee;
    width: 100%;
    height: 36px;
    line-height: 36px;
    font-family: Averta;
    font-size: 12px;
    margin-top: 8px;
    padding: 0 10px;
    appearance: none;

    @media (max-width: 30em) {
        height: 52px;
        line-height: 52px;
        font-size: 14px;
    }

    &:focus {
        outline: none;
        border: 1px solid #ff245e;
    }
`;

const Button = styled.div`
    position: absolute;
    right: -1px;
    bottom: 1px;
    height: 34px;
    line-height: 32px;
    padding: 0 10px;

    @media (max-width: 30em) {
        height: 50px;
        line-height: 50px;
    }

    &:before {
        content: '';
        width: 100px;
        height: 100%;
        float: left;
        background-image: linear-gradient(to right, rgba(255, 255, 255, 0), #ffffff);
    }

    button {
        border: 0;
        color: #ff245e;
        font-size: 14px;
        display: inline-block;
        background: #fff;
        cursor: pointer;
        font-weight: 600;
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

        &:hover,
        &:focus {
            outline: none;
        }
    }
`;

const CloseButton = styled.div`
    width: 14px;
    height: 26px;
    transform: rotate(45deg);
    font-family: Averta;
    font-size: 24px;
    line-height: 1.08;
    text-align: left;
    color: #5d656c;
    margin-top: -0.625em;
    cursor: pointer;
    position: absolute;
    right: 18px;
    top: 28px;
`;

const Span = styled.div`
    font-size: 10px;
    color: #000;
    font-weight: bold;
    line-height: 8px;

    a {
        font-size: 10px;
        font-weight: normal;
        margin: 0 15px 0 5px;
        padding: 0;
        ${() => isPortrait() && `margin-left: 0;`};

        &:last-child {
            margin-right: 0;
        }
    }
`;

const Mask = styled.div`
    position: absolute;
    background-color: #fff;
    width: 20px;
    height: 16px;
    left: -12.25px;
    z-index: 1;
`;

const Link = styled.div`
    font-family: Lato;
    font-size: 10px;
    line-height: 1.33;
    color: #008eff;
    font-weight: normal;
    cursor: pointer;
    display: inline;
    margin: 0 15px 0 5px;
`;

const NoShareBottomDrawer = props => (
    <Wrapper
        shareDrawerVisibility={props.shareDrawerVisibility}
        backgroundColor={props.backgroundColor}
        hasNextAction={props.nextAction !== null}
    >
        <Container {...props}>
            {props.children}
            {props.localRepFound && props.useLocalRepresentative && (
                <Span>
                    {copyFor('findReps', 'notYour')}&nbsp;&nbsp;
                    <Link onClick={props.onFindYourRep}>
                        {isPortrait()
                            ? copyFor('findReps', 'clickToFindShort')
                            : copyFor('findReps', 'clickToFind')}
                    </Link>
                </Span>
            )}
            {props.isEndCard && !props.isEmailSubmitted && (
                <Span>
                    {!isPortrait() && copyFor('footer', 'stayInformed')}&nbsp;
                    {!isPortrait() && props.width < 350 && <br />}
                    <Link onClick={() => props.setOverlayVisible(true)}>
                        {copyFor('footer', 'newsletterSignup')}
                    </Link>
                </Span>
            )}
            <Span>
                <Link
                    href="https://actionbutton.org/terms"
                    rel="noopener noreferrer"
                    target="_blank"
                    onClick={proxy => proxy.stopPropagation()}
                >
                    {copyFor('footer', 'terms')}
                </Link>
            </Span>
        </Container>
    </Wrapper>
);

const ShareBottomDrawer = props => (
    <Wrapper
        shareDrawerVisibility={props.shareDrawerVisibility}
        backgroundColor={props.backgroundColor}
        hasNextAction={props.nextAction !== null}
    >
        <ShareBackground
            shareDrawerVisibility={props.shareDrawerVisibility}
            onClick={() => props.closeShareDrawer()}
        />
        {props.shareDrawerVisibility && (
            <ShareDrawerContent drawerHeight={widthEm() > 30 ? `-30px` : `-88px`}>
                <ShareSection>
                    <Title>{copyFor('footer', 'share')}</Title>
                    <SocialShareContainer>
                        <SocialShareGroup
                            isEmbed={props.isEmbed}
                            shareUrl={props.shareUrl}
                            widgetRequestId={props.widgetRequestId}
                            twitterMention={props.twitterMention}
                            publisherName={props.publisherName}
                            partnerName={props.partnerName}
                            actionType={props.actionType}
                            actionShared={props.actionShared}
                        />
                    </SocialShareContainer>
                </ShareSection>
                <EmbedSection>
                    <Title>{copyFor('footer', 'embed')}</Title>
                    <Input id="embedIframe" defaultValue={props.iframeSnippet} readOnly />
                    <Button>
                        <Clipboard
                            data-clipboard-text={props.iframeSnippet}
                            onClick={() => props.copyToClipboard()}
                        >
                            {props.copiedToClipboard
                                ? copyFor('footer', 'copied')
                                : copyFor('footer', 'copy')}
                        </Clipboard>
                    </Button>
                </EmbedSection>
                <CloseButton onClick={() => props.closeShareDrawer()}>+</CloseButton>
            </ShareDrawerContent>
        )}
        <Container {...props}>
            {props.hasBackButton && props.goBack && <Mask />}
            {props.hasBackButton && props.goBack && (
                <BackButton
                    newsletterSignUpVisible={!props.isEndCard}
                    onClick={e => {
                        e.stopPropagation();
                        props.goBack();
                    }}
                />
            )}
            {props.children}
            {!props.newsletterSignUpVisible &&
                props.localRepFound &&
                props.useLocalRepresentative && (
                    <Span>
                        {copyFor('findReps', 'notYour')}&nbsp;&nbsp;
                        <Link onClick={props.onFindYourRep}>
                            {isPortrait()
                                ? copyFor('findReps', 'clickToFindShort')
                                : copyFor('findReps', 'clickToFind')}
                        </Link>
                    </Span>
                )}
            {props.newsletterSignUpVisible && !props.isEmailSubmitted && (
                <Span>
                    <span className="emailSignup">
                        {!isPortrait() && copyFor('footer', 'stayInformed')}&nbsp;
                        {!isPortrait() && props.width < 350 && <br />}
                        <Link onClick={() => props.setOverlayVisible(true)}>
                            {copyFor('footer', 'newsletterSignup')}
                        </Link>
                    </span>
                </Span>
            )}
            <Span>
                <Link onClick={() => props.openShareDrawer()}>
                    <ShareIcon /> {copyFor('footer', 'share')}
                </Link>
                <Link
                    href="https://actionbutton.org/terms"
                    rel="noopener noreferrer"
                    target="_blank"
                    onClick={proxy => proxy.stopPropagation()}
                >
                    {copyFor('footer', 'terms')}
                </Link>
            </Span>
        </Container>
    </Wrapper>
);

const sharedProptypes = {
    actionShared: PropTypes.func,
    actionType: PropTypes.string,
    backgroundColor: PropTypes.string,
    children: PropTypes.object,
    closeShareDrawer: PropTypes.func,
    copiedToClipboard: PropTypes.bool,
    copyToClipboard: PropTypes.func,
    iframeSnippet: PropTypes.string,
    isActionChoiceCard: PropTypes.bool.isRequired,
    isEmailSubmitted: PropTypes.bool,
    isEmbed: PropTypes.bool.isRequired,
    localRepFound: PropTypes.bool,
    newsletterSignUpVisible: PropTypes.bool,
    nextAction: PropTypes.object,
    onFindYourRep: PropTypes.func.isRequired,
    openShareDrawer: PropTypes.func,
    partnerName: PropTypes.string,
    publisherName: PropTypes.string,
    setOverlayVisible: PropTypes.func,
    shareDrawerVisibility: PropTypes.bool,
    twitterMention: PropTypes.string,
    useLocalRepresentative: PropTypes.bool.isRequired,
    widgetRequestId: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired
};

NoShareBottomDrawer.propTypes = { ...sharedProptypes };
ShareBottomDrawer.propTypes = { ...sharedProptypes };

const ShareDrawer = staticData.useStatic ? NoShareBottomDrawer : ShareBottomDrawer;

export default connect(
    (state, props) => {
        const nextAction = getNextAction(
            state.widget.actions,
            state.widget.activeAction.identifier,
            buildConditionalActionsAnswerHistory(state),
            state.widget.cards
        );

        const currentCard = state.widget.cards.find(
            c => c.id === state.widget.activeAction.widgetCardId
        );

        return {
            shareUrl: state.widget.articleUrl,
            newsletterSignUpVisible:
                props.newsletterSignUpVisible && state.widget.showNewsletterSignup,
            copiedToClipboard: state.widget.textCopiedToClipboard,
            iframeSnippet: `<iframe style="width: 100%" frameborder="0" scrolling="no" height="450" src="https://embed.actionbutton.co/widget/widget-iframe.html?widgetId=${
                state.widget.widgetRequestId
            }"></iframe>`,
            isActionChoiceCard: currentCard.type === CardTypes.actionChoice,
            isEmailSubmitted: state.widget.isEmailSubmitted,
            isEmbed: state.widget.articleUrl.includes('embed'),
            localRepFound: state.localRep.localRepData.representative !== undefined,
            nextAction,
            shareDrawerVisibility: state.widget.shareDrawerVisibility,
            useLocalRepresentative: !!state.widget.activeAction.representativeSearch,
            widgetRequestId: state.widget.widgetRequestId,
            width: width()
        };
    },
    {
        closeShareDrawer,
        copyToClipboard,
        onFindYourRep,
        openShareDrawer,
        setOverlayVisible
    }
)(ShareDrawer);
